import { useCallback, useEffect, useLayoutEffect, useRef, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { AVAILABLE_ANIMATIONS } from '@/constants/animations';
import { animationPreviewUrl } from '@/lib/subtitles/animationPreviewVideo';
import { cn } from '@/lib/utils';

/**
 * Pre-rendered loop of the animation (one fixed look for every card). The
 * video loads the first time its card scrolls into view and only plays while
 * visible, so the strip costs a handful of small videos, not 37 live Players.
 */
const AnimationPreviewVideo = ({ animationName }: { animationName: string }) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [visible, setVisible] = useState(false);
  const [requested, setRequested] = useState(false);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => setVisible(Boolean(entries[0]?.isIntersecting)),
      { threshold: 0.25 },
    );
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (visible) setRequested(true);
  }, [visible]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (visible) {
      video.play()?.catch(() => {});
    } else {
      video.pause();
    }
  }, [visible, requested]);

  return (
    <div ref={hostRef} className="relative h-14 w-full">
      {requested && (
        <video
          ref={videoRef}
          src={animationPreviewUrl(animationName)}
          className="pointer-events-none h-full w-full object-cover"
          muted
          loop
          playsInline
          autoPlay
          preload="auto"
          aria-hidden
        />
      )}
    </div>
  );
};

interface SubtitleAnimationCarouselProps {
  value: string;
  onChange: (name: string) => void;
}

export const SubtitleAnimationCarousel = ({
  value,
  onChange,
}: SubtitleAnimationCarouselProps) => {
  const { t } = useTranslation();
  const selectedRef = useRef<HTMLDivElement | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const isDragging = useRef(false);
  const hasDragged = useRef(false);
  const dragStartX = useRef(0);
  const scrollStartX = useRef(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollEdges = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    setCanScrollLeft(scroller.scrollLeft > 1);
    setCanScrollRight(scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1);
  }, []);

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    updateScrollEdges();
    scroller.addEventListener('scroll', updateScrollEdges, { passive: true });
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateScrollEdges);
    observer?.observe(scroller);
    return () => {
      scroller.removeEventListener('scroll', updateScrollEdges);
      observer?.disconnect();
    };
  }, [updateScrollEdges]);

  const endDrag = () => {
    isDragging.current = false;
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.style.cursor = 'grab';
    scroller.style.removeProperty('user-select');
    scroller.style.removeProperty('scroll-snap-type');
  };

  useEffect(() => {
    const onMove = (event: globalThis.MouseEvent) => {
      if (!isDragging.current || !scrollerRef.current) return;
      const dx = event.clientX - dragStartX.current;
      if (Math.abs(dx) > 3) hasDragged.current = true;
      scrollerRef.current.scrollLeft = scrollStartX.current - dx;
    };
    const onUp = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      const scroller = scrollerRef.current;
      if (!scroller) return;
      scroller.style.cursor = 'grab';
      scroller.style.removeProperty('user-select');
      scroller.style.removeProperty('scroll-snap-type');
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  useEffect(() => {
    selectedRef.current?.scrollIntoView({
      inline: 'center',
      block: 'nearest',
      behavior: 'smooth',
    });
  }, [value]);

  const scrollByDirection = (direction: -1 | 1) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const amount = Math.max(scroller.clientWidth * 0.7, 124);
    scroller.scrollBy({ left: direction * amount, behavior: 'smooth' });
  };

  const handleMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    if (event.button !== 0 || !scrollerRef.current) return;
    isDragging.current = true;
    hasDragged.current = false;
    dragStartX.current = event.clientX;
    scrollStartX.current = scrollerRef.current.scrollLeft;
    scrollerRef.current.style.cursor = 'grabbing';
    scrollerRef.current.style.userSelect = 'none';
    scrollerRef.current.style.scrollSnapType = 'none';
  };

  const handleMouseMove = (event: MouseEvent<HTMLDivElement>) => {
    if (!isDragging.current || !scrollerRef.current) return;
    const dx = event.clientX - dragStartX.current;
    if (Math.abs(dx) > 3) hasDragged.current = true;
    scrollerRef.current.scrollLeft = scrollStartX.current - dx;
  };

  const selectAnimation = (name: string) => {
    if (hasDragged.current) return;
    onChange(name);
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 px-4">
        <Sparkles className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-semibold">
          {t('configureSubtitles.editor.animation')}
        </span>
      </div>
      <div className="relative">
        <div
          className="pointer-events-none absolute inset-y-0 left-0 z-10 w-7 bg-gradient-to-r from-background to-transparent"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-y-0 right-0 z-10 w-7 bg-gradient-to-l from-background to-transparent"
          aria-hidden
        />
        <button
          type="button"
          data-testid="subtitle-animation-carousel-prev"
          aria-label={t('configureSubtitles.editor.animationPrevious')}
          disabled={!canScrollLeft}
          onClick={() => scrollByDirection(-1)}
          className={cn(
            'absolute left-1 top-1/2 z-20 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background/95 shadow-sm',
            canScrollLeft ? 'opacity-100' : 'pointer-events-none opacity-0',
          )}
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          data-testid="subtitle-animation-carousel-next"
          aria-label={t('configureSubtitles.editor.animationNext')}
          disabled={!canScrollRight}
          onClick={() => scrollByDirection(1)}
          className={cn(
            'absolute right-1 top-1/2 z-20 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background/95 shadow-sm',
            canScrollRight ? 'opacity-100' : 'pointer-events-none opacity-0',
          )}
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
        <div
          ref={scrollerRef}
          data-testid="subtitle-animation-carousel"
          className="flex cursor-grab gap-2 overflow-x-auto snap-x snap-mandatory px-4 pb-1 scrollbar-hide"
          style={{ scrollbarWidth: 'none' }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={endDrag}
          onMouseLeave={endDrag}
        >
          {AVAILABLE_ANIMATIONS.map((animation) => {
            const isSelected = value === animation.name;
            return (
              <div
                key={animation.name}
                ref={isSelected ? selectedRef : undefined}
                role="button"
                tabIndex={0}
                data-testid={`subtitle-animation-${animation.name}`}
                aria-pressed={isSelected}
                onClick={() => selectAnimation(animation.name)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    selectAnimation(animation.name);
                  }
                }}
                className={cn(
                  'relative w-[7.25rem] shrink-0 snap-start overflow-hidden rounded-lg border-2 bg-black text-left transition-all',
                  isSelected
                    ? 'border-primary shadow-[0_0_8px_-2px] shadow-primary/40'
                    : 'border-white/5 hover:border-white/15',
                )}
              >
                <AnimationPreviewVideo animationName={animation.name} />
                <div
                  className={cn(
                    'px-1 py-0.5 text-center',
                    isSelected ? 'bg-primary/15' : 'bg-white/[0.03]',
                  )}
                >
                  <p className="truncate text-[9px] font-medium text-white/80">
                    {animation.name}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
